import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useSupercluster from 'use-supercluster';
import { Row, Col } from 'react-bootstrap';
import { Maximize, Minimize } from 'lucide-react';
import { Text, Button } from 'v3/components';
import theme from 'v3/theme';
import { FullscreenButton, MapsContainer, MapButtonsCol } from './styles';
import { DefaultLoadingComponent } from 'v3/components/List';
import DetailsCard from './DetailCard';

const DEFAULT_CENTER = [-14.235, -51.9253];
const DEFAULT_ZOOM = 4;

function createPinIcon(background, sizeIcon = 22, color = '#fff') {
  const isSvg = String(background).includes('.svg');
  const backgroundCss = isSvg
    ? `background-image: url(/images/${background}); background-size: 100% 100%;`
    : `background: ${background};`;

  return L.divIcon({
    className: '',
    iconSize: [sizeIcon, sizeIcon],
    iconAnchor: [sizeIcon / 2, sizeIcon],
    html: `
      <div style="
        width: ${sizeIcon}px;
        height: ${sizeIcon}px;
        border-radius: 0% 50% 50% 50%;
        ${backgroundCss}
        transform: rotate(-135deg);
        transform-origin: top left;
        position: absolute;
        left: 50%;
        top: 50%;
        margin: 0;
      ">
        <div style="
          width: 14px;
          height: 14px;
          margin: 10px 0 0 10px;
          transform: rotate(135deg);
          border-radius: 50%;
          color: ${color};
        "></div>
      </div>
    `,
  });
}

function createClusterIcon(pointCount, totalPoints) {
  const iconSize = 10 + (pointCount / Math.max(totalPoints, 1)) * 20;
  return L.divIcon({
    className: '',
    html: `
      <div style="
        width: ${iconSize}px;
        height: ${iconSize}px;
        color: #fff;
        background: #1978c8;
        border-radius: 50%;
        padding: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 12px;
        font-weight: 700;
      ">${pointCount}</div>
    `,
  });
}

function MapEventsHandler({ setBounds, setZoom, setChecked, mapRef }) {
  const map = useMapEvents({
    click: () => setChecked(false),
    zoomend: () => {
      const mapBounds = map.getBounds();
      setZoom(map.getZoom());
      setBounds([mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()]);
    },
    moveend: () => {
      const mapBounds = map.getBounds();
      setBounds([mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()]);
    },
  });

  useEffect(() => {
    mapRef.current = map;
    const mapBounds = map.getBounds();
    setZoom(map.getZoom());
    setBounds([mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()]);
  }, [map, mapRef, setBounds, setZoom]);

  return null;
}

function ResizeController({ trigger }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return undefined;

    map.invalidateSize(true);

    const timeout = setTimeout(() => {
      map.invalidateSize(true);
    }, 180);

    return () => clearTimeout(timeout);
  }, [map, trigger]);

  return null;
}

export default function MapCluster({
  travelsMapStatus,
  toggleMaps,
  branchOffices,
  queryParams,
  setToggleMaps,
  loading,
  filterTravelVehicle,
  setFilterTravelVehicle,
  checked,
  setChecked,
}) {
  const [detail, setDetail] = useState([]);
  const mapRef = useRef();
  const mapContainerRef = useRef(null);
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(0);
  const [fullScreen, setFullScreen] = useState(false);
  const { travelPoints, stationPoints } = useMemo(() => {
    if (travelsMapStatus && branchOffices) {
      const ids = queryParams == null ? queryParams :
        queryParams.includes(';')
          ? queryParams.split(';')
          : [queryParams];

      const pTravels = travelsMapStatus
        ?.filter(t => t.latitude && t.longitude)
        ?.map(travel => {
          return ({
            type: 'Feature',
            properties: {
              cluster: false,
              id: travel.travelId,
              category: travel.travelStatus,
            },
            geometry: {
              type: 'Point',
              coordinates: [
                parseFloat(travel?.longitude),
                parseFloat(travel?.latitude),
              ],
            },
          })
        });

      const pBranchOffices = branchOffices
        ?.filter(t => t.latitude && t.longitude && (ids == null || (ids != null && ids.some(id => id == t.id))))
        ?.map(branchOffice => {
          return ({
            type: 'Feature',
            properties: {
              cluster: false,
              id: branchOffice.id,
              category: 'station',
            },
            geometry: {
              type: 'Point',
              coordinates: [
                parseFloat(branchOffice?.longitude),
                parseFloat(branchOffice?.latitude),
              ],
            },
          })
        });

      return {
        travelPoints: pTravels,
        stationPoints: pBranchOffices,
      };
    }

    return {
      travelPoints: [],
      stationPoints: [],
    };
  }, [travelsMapStatus, branchOffices, queryParams]);

  const { clusters, supercluster } = useSupercluster({
    points: travelPoints,
    bounds,
    zoom,
    options: { radius: 60, minZoom: 0, maxZoom: 20, minPoints: 4 },
  });

  function retrieveDetail(id, type) {
    const data =
      type === 'travel'
        ? travelsMapStatus.find(t => String(t.travelId) === String(id))
        : branchOffices.find(t => String(t.id) === String(id));

    if (!data) return;

    setChecked(true);
    setDetail({ ...data, type });
  }

  function stopMarkerClickPropagation(event) {
    if (event?.originalEvent) {
      L.DomEvent.stopPropagation(event.originalEvent);
    }
  }

  const toggleFullScreen = useCallback(() => {
    const element = mapContainerRef.current;
    if (!element) return;

    const fullscreenElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (fullscreenElement) {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;

      if (exit) exit.call(document);
      return;
    }

    const enter =
      element.requestFullscreen ||
      element.webkitRequestFullscreen ||
      element.mozRequestFullScreen ||
      element.msRequestFullscreen;

    if (enter) enter.call(element);
  }, []);

  useEffect(() => {
    function handleFullScreenChange() {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      setFullScreen(fullscreenElement === mapContainerRef.current);
    }

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);
    document.addEventListener('MSFullscreenChange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullScreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullScreenChange);
    };
  }, []);

  return (
    <MapsContainer>
      <Row className="d-flex align-items-center">
        <MapButtonsCol>
          <Button onClick={() => setToggleMaps(!toggleMaps)}>
            <Text type="regular" weight={500} fontSize="0.75rem">
              {toggleMaps ? 'Ver mapa' : 'Minimizar'}
            </Text>
          </Button>
          {filterTravelVehicle ? (
            <Button
              onClick={() => setFilterTravelVehicle(0)}
              variant="secondary"
            >
              <Text type="regular" weight={500} fontSize="0.75rem">
                {'Limpar seleção'}
              </Text>
            </Button>
          ) : null}
        </MapButtonsCol>
      </Row>
      <>
        {toggleMaps === false && loading === false && travelPoints ? (
          <Row style={{ flex: 1, minHeight: 0 }}>
            <Col style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div
                ref={mapContainerRef}
                style={{
                  flex: 1,
                  width: fullScreen ? '100%' : '150%',
                  height: fullScreen ? '100vh' : 'auto',
                  position: 'relative',
                  minHeight: fullScreen ? '100vh' : 0,
                  background: '#fff',
                }}
              >
                  <FullscreenButton
                    aria-label={fullScreen ? 'Sair da tela cheia' : 'Ver em tela cheia'}
                    title={fullScreen ? 'Sair da tela cheia' : 'Tela cheia'}
                    type="button"
                    onClick={toggleFullScreen}
                  >
                    {fullScreen ? <Minimize size={18} /> : <Maximize size={18} />}
                  </FullscreenButton>
                  <MapContainer
                    center={DEFAULT_CENTER}
                    zoom={DEFAULT_ZOOM}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom
                  >
                    <TileLayer
                      attribution='&copy; OpenStreetMap contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapEventsHandler
                      setBounds={setBounds}
                      setZoom={setZoom}
                      setChecked={setChecked}
                      mapRef={mapRef}
                    />
                    <ResizeController trigger={fullScreen} />
                    {clusters.map((cluster) => {
                    const [longitude, latitude] = cluster.geometry.coordinates;
                    const {
                      cluster: isCluster,
                      point_count: pointCount,
                      category,
                    } = cluster.properties;
                    if (isCluster) {
                      return (
                        <Marker
                          key={`cluster-${cluster.id}`}
                          position={[latitude, longitude]}
                          icon={createClusterIcon(pointCount, travelPoints?.length)}
                          eventHandlers={{
                            click: (event) => {
                              stopMarkerClickPropagation(event);
                              const expansionZoom = Math.min(
                                supercluster.getClusterExpansionZoom(cluster.id),
                                20,
                              );
                              mapRef.current.setView([latitude, longitude], expansionZoom);
                            },
                          }}
                        >
                        </Marker>
                      );
                    }
                    if (!latitude || !longitude) {
                      return false;
                    }
                    return (
                      <Marker
                        key={cluster.properties.id}
                        position={[latitude, longitude]}
                        icon={createPinIcon(
                          category === 'station'
                            ? 'posto-pen.svg'
                            : category === 'onTime'
                            ? theme.colors.success
                            : category === 'delayed'
                            ? theme.colors.danger
                            : theme.colors.orange,
                          category === 'station' ? 30 : 22,
                          '#fff',
                        )}
                        eventHandlers={{
                          click: (event) => {
                            stopMarkerClickPropagation(event);
                            if (category === 'station') {
                              retrieveDetail(cluster.properties.id, 'station');
                            } else {
                              retrieveDetail(cluster.properties.id, 'travel');
                            }
                          },
                        }}
                      />
                    );
                  })}
                    {stationPoints.map((station) => {
                      const [longitude, latitude] = station.geometry.coordinates;
                      if (!latitude || !longitude) {
                        return false;
                      }

                      return (
                        <Marker
                          key={`station-${station.properties.id}`}
                          position={[latitude, longitude]}
                          icon={createPinIcon('posto-pen.svg', 30, '#fff')}
                          eventHandlers={{
                            click: (event) => {
                              stopMarkerClickPropagation(event);
                              retrieveDetail(station.properties.id, 'station');
                            },
                          }}
                        />
                      );
                    })}
                  </MapContainer>
                {checked ? (
                  <DetailsCard
                    load={detail}
                    checked={checked}
                    onClose={() => setChecked(false)}
                  />
                ) : null}
              </div>
            </Col>
          </Row>
        ) : toggleMaps === true ? null : (
          <DefaultLoadingComponent />
        )}
      </>
    </MapsContainer>
  );
}
